import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { courses } from '../services/api';

interface PaceStatistic {
  // Updated to match the new field name from the backend
  question_flow?: string;
  previous_path?: string; // Keep for backward compatibility
  median_response_time_s: number;
  ninetieth_perc_response_time_s: number;
}

interface PaceReportProps {
  organizationId?: string;
}

export const PaceReport: React.FC<PaceReportProps> = ({ organizationId }) => {
  const [data, setData] = useState<PaceStatistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('[PaceReport] Fetching data');
        const response = await courses.getUserPaceStats(organizationId);
        console.log('[PaceReport] Data received:', response);
        setData(response.results);
      } catch (err) {
        console.error('Error fetching pace statistics data:', err);
        setError('Failed to load pace statistics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  // Format path for better readability
  const formatPath = (path: string | undefined): string => {
    // Handle undefined or empty paths
    if (!path) {
      return 'Unknown Path';
    }
    
    // Remove API prefix and trailing slashes
    let formattedPath = path.replace(/^\/api\//, '').replace(/\/$/, '');
    
    // Replace underscores with spaces and capitalize words
    formattedPath = formattedPath
      .replace(/_/g, ' ')
      .replace(/\//g, ' > ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    
    return formattedPath;
  };

  // Format seconds to minutes and seconds
  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} sec`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} min ${remainingSeconds} sec`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <Typography color="error">{error || 'No pace data available'}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="pace statistics table">
        <TableHead>
          <TableRow>
            <TableCell><Typography variant="subtitle1" fontWeight="bold">Question Path</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle1" fontWeight="bold">Median Response Time Per Question</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle1" fontWeight="bold">90th Percentile Response Time Per Question</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component="th" scope="row">
                {formatPath(row.question_flow || row.previous_path)}
              </TableCell>
              <TableCell align="right">{formatTime(row.median_response_time_s)}</TableCell>
              <TableCell align="right">{formatTime(row.ninetieth_perc_response_time_s)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PaceReport;
